import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

// Exactly one photo: keep in step with PROOF_IMAGE_MIN / PROOF_IMAGE_MAX in
// staff_backend/Controller/Task/TaskController.js.
const PROOF_MIN = 1;
const PROOF_MAX = 1;
const MAX_FILE_MB = 10;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

const TaskCompleteModal = ({ task, canOverride = false, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const [otp, setOtp] = useState("");
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false);
    const [override, setOverride] = useState(false);
    const [reason, setReason] = useState("");

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const previews = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
    useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)));

    const addFiles = (event) => {
        const picked = Array.from(event.target.files || []);
        event.target.value = "";
        const file = picked[0];
        if (!file) return;
        if (!ALLOWED.includes(file.type)) { toast.error(`${file.name}: only JPG, PNG or WebP photos`); return; }
        if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`${file.name}: larger than ${MAX_FILE_MB} MB`); return; }
        setFiles([file]);
    };

    // The backend says whether the customer's code is required (TaskUtils.HANDOVER_CODE_ENABLED).
    const codeRequired = task.handoverCodeRequired === true;
    const otpValid = /^\d{4}$/.test(otp);
    const ready = override ? reason.trim().length >= 5 : (!codeRequired || otpValid) && files.length >= PROOF_MIN;

    const onSubmit = async () => {
        if (!ready) return;
        const formData = new FormData();
        if (override) {
            formData.append("override", "true");
            formData.append("reason", reason.trim());
        } else {
            if (codeRequired) formData.append("otp", otp);
            files.forEach((file) => formData.append("proofImages", file));
        }
        setSaving(true);
        const response = await Handler({ method: "patch", url: `/staff/task/complete/${task.taskId}`, data: formData, formdata: true });
        setSaving(false);
        if (response?.success) {
            toast.success(response?.data?.message || "Task completed");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to complete this task"));
        }
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">Complete {task.taskType?.toLowerCase()} {task.taskId}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body d-flex flex-column gap-3">
                        {/* {canOverride ? (
                            <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="task-override" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                                <label className="form-check-label small" htmlFor="task-override">{codeRequired ? "Complete without the customer's code and photos (support override)" : "Complete without photos (support override)"}</label>
                            </div>
                        ) : ""} */}
                        {override ? (
                            <div>
                                <label className="form-label">Reason</label>
                                <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={codeRequired ? "Why is this handover being completed without the code?" : "Why is this task being completed without photos?"} />
                            </div>
                        ) : (<>
                        {codeRequired ? (<div>
                            <label className="form-label">Customer's 4-digit code</label>
                            <input
                                className={`form-control ${otp && !otpValid ? "is-invalid" : ""}`}
                                inputMode="numeric"
                                maxLength={4}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                placeholder="Ask the customer for the code in their app"
                            />
                        </div>) : null}
                        <div>
                            <label className="form-label">Completion photo (one photo)</label>
                            <div className="d-flex flex-wrap gap-2">
                                {previews.map((p, i) => (
                                    <div key={p.url} className="position-relative">
                                        <img src={p.url} alt={`Handover ${i + 1}`} style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 8 }} />
                                        <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 p-1" style={{ lineHeight: 1 }} onClick={() => setFiles(files.filter((_, idx) => idx !== i))} aria-label="Remove photo"><ImCross size={8} /></button>
                                    </div>
                                ))}
                                {files.length < PROOF_MAX ? (
                                    <label className="d-flex flex-column align-items-center justify-content-center border rounded-3 text-muted" style={{ width: 84, height: 84, cursor: "pointer", borderStyle: "dashed" }}>
                                        <FaCloudUploadAlt size={22} />
                                        <span className="small">Add</span>
                                        <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={addFiles} />
                                    </label>
                                ) : ""}
                            </div>
                        </div>
                        </>)}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" disabled={saving}>Back</button>
                        <button type="button" className="btn btn-success" onClick={onSubmit} disabled={!ready || saving}>
                            {saving ? "Completing..." : "Complete handover"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaskCompleteModal;
